import { SNAPSHOT_MOVEMENT_STEP_GOAL_DEFAULT } from "@healthos/shared";
import type { AttentionWatchingItem } from "./types";

export type SignalMetricTone = "steady" | "watch";

export interface SignalMetricReading {
  key: string;
  label: string;
  value: string;
  unit: string;
  status: string;
  tone: SignalMetricTone;
  markerPct: number;
  rangeStartPct: number;
  rangeEndPct: number;
}

export type InterpretedWatchingItem =
  | { kind: "metric"; metric: SignalMetricReading }
  | { kind: "plain"; row: AttentionWatchingItem };

export function describeWatchingItem(row: AttentionWatchingItem): InterpretedWatchingItem {
  const metric = metricForWatchingItem(row);
  return metric ? { kind: "metric", metric } : { kind: "plain", row };
}

/**
 * What a row measures, taken from the signal kind when the row carries one.
 * The label is member-facing copy: matching on it made any row whose wording
 * happened to contain a metric word get rewritten into that metric, which is
 * how a count of "Microsteps" ended up rendered against a pedometer goal.
 * Rows without a kind still fall back to the label so older callers keep
 * working.
 */
const METRIC_BY_SIGNAL_KIND: Record<string, string> = {
  "wearable.sleep": "sleep",
  "wearable.hrv": "hrv",
  "wearable.rhr": "resting hr",
  "wearable.activity": "steps",
  "wearable.bp.systolic": "systolic",
  "wearable.bp.diastolic": "diastolic",
  "hydration.ml": "hydration",
};

function metricForWatchingItem(row: AttentionWatchingItem): SignalMetricReading | null {
  const fromKind = row.kind ? METRIC_BY_SIGNAL_KIND[row.kind] : undefined;
  // A row that names its kind but is not a metric kind is not a reading, and
  // must not be reclassified by whatever its label happens to say.
  if (row.kind && !fromKind) return null;
  const label = fromKind ?? row.label.toLowerCase();

  if (label.includes("sleep")) {
    const value = extractSleepHours(row.value);
    if (value === null) return null;
    return metricFromRange({
      key: row.label,
      label: "Sleep",
      value,
      unit: "h",
      decimals: 1,
      min: 4,
      max: 10,
      rangeStart: 7,
      rangeEnd: 9,
      status: value < 6 ? "Short" : value < 7 ? "Light" : "On track",
      tone: value < 7 ? "watch" : "steady",
    });
  }

  const value = extractFirstNumber(row.value);
  if (value === null) return null;

  if (label.includes("hrv")) {
    return metricFromRange({
      key: row.label,
      label: "HRV",
      value,
      unit: "ms",
      min: 10,
      max: 90,
      rangeStart: 30,
      rangeEnd: 70,
      status: value < 30 ? "Low" : "In range",
      tone: value < 30 ? "watch" : "steady",
    });
  }

  if (label.includes("systolic")) {
    return metricFromRange({
      key: row.label,
      label: "AM systolic",
      value,
      unit: "mmHg",
      min: 90,
      max: 170,
      rangeStart: 90,
      rangeEnd: 129,
      status: value >= 130 ? "Elevated" : "In range",
      tone: value >= 130 ? "watch" : "steady",
    });
  }

  if (label.includes("diastolic")) {
    return metricFromRange({
      key: row.label,
      label: "AM diastolic",
      value,
      unit: "mmHg",
      min: 50,
      max: 110,
      rangeStart: 60,
      rangeEnd: 79,
      status: value >= 80 ? "Elevated" : "In range",
      tone: value >= 80 ? "watch" : "steady",
    });
  }

  if (label.includes("resting hr")) {
    const isOutOfRange = value < 50 || value > 80;
    return metricFromRange({
      key: row.label,
      label: "Resting HR",
      value,
      unit: "bpm",
      min: 40,
      max: 110,
      rangeStart: 50,
      rangeEnd: 80,
      status: value < 50 ? "Low" : value > 80 ? "Elevated" : "In range",
      tone: isOutOfRange ? "watch" : "steady",
    });
  }

  if (label.includes("hydration")) {
    return metricFromRange({
      key: row.label,
      label: "Hydration",
      value,
      unit: "L",
      decimals: 1,
      min: 0,
      max: 3.5,
      rangeStart: 2,
      rangeEnd: 3,
      status: value < 1.5 ? "Low" : value < 2 ? "Building" : "On track",
      tone: value < 2 ? "watch" : "steady",
    });
  }

  // Word-boundary, not substring: "Microsteps" contains "steps" and was being
  // rewritten into the movement metric, rendering a count of microsteps
  // against a 12,000-step pedometer goal.
  if (/\bsteps\b/.test(label)) {
    return metricFromRange({
      key: row.label,
      label: "Steps",
      value,
      unit: "",
      min: 0,
      max: 12000,
      rangeStart: SNAPSHOT_MOVEMENT_STEP_GOAL_DEFAULT,
      rangeEnd: 10000,
      status:
        value < 5000
          ? "Building"
          : value < SNAPSHOT_MOVEMENT_STEP_GOAL_DEFAULT
            ? "Moving"
            : "On track",
      tone: value < SNAPSHOT_MOVEMENT_STEP_GOAL_DEFAULT ? "watch" : "steady",
    });
  }

  return null;
}

function metricFromRange({
  decimals = 0,
  key,
  label,
  max,
  min,
  rangeEnd,
  rangeStart,
  status,
  tone,
  unit,
  value,
}: {
  decimals?: number;
  key: string;
  label: string;
  max: number;
  min: number;
  rangeEnd: number;
  rangeStart: number;
  status: string;
  tone: SignalMetricTone;
  unit: string;
  value: number;
}): SignalMetricReading {
  return {
    key,
    label,
    value: decimals === 0 ? String(Math.round(value)) : value.toFixed(decimals),
    unit,
    status,
    tone,
    markerPct: normalizePct(value, min, max),
    rangeStartPct: normalizePct(rangeStart, min, max),
    rangeEndPct: normalizePct(rangeEnd, min, max),
  };
}

function normalizePct(value: number, min: number, max: number): number {
  if (max <= min) return 0;
  const pct = ((value - min) / (max - min)) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

// The whole value, with at most a unit after it. A number inside a time or a
// sentence ("8:26am", "since 11am") is not a reading of the row's kind.
function extractFirstNumber(value: string): number | null {
  const match = /^\s*(-?\d[\d,]*(?:\.\d+)?)\s*[a-zA-Z%/]*\s*$/.exec(value);
  if (!match) return null;
  const parsed = Number.parseFloat((match[1] ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function extractSleepHours(value: string): number | null {
  const hoursMatch = /(\d+(?:\.\d+)?)\s*h/i.exec(value);
  if (!hoursMatch) return extractFirstNumber(value);
  const hours = Number.parseFloat(hoursMatch[1] ?? "");
  if (!Number.isFinite(hours)) return null;
  const minutesMatch = /(\d+(?:\.\d+)?)\s*m/i.exec(value);
  const minutes = minutesMatch ? Number.parseFloat(minutesMatch[1] ?? "") : 0;
  return Number.isFinite(minutes) ? hours + minutes / 60 : hours;
}
